import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Coins, Sparkles, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LOW_BALANCE_THRESHOLD } from "@/lib/nudges/nudge.types";
import type { NudgeId } from "@/lib/nudges/nudge.types";
import { useUpgradeNudge } from "@/lib/nudges/useUpgradeNudge.hook";

/**
 * Soft (low balance) and hard (out of coins) dashboard cards for free users.
 * Soft fires around half of the free grant; hard when balance is zero.
 */
export function LowBalanceNudgeCard() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		eligible,
		canShow,
		markShown,
		markClicked,
		markDismissed,
		coinBalance,
	} = useUpgradeNudge();
	const [activeNudge, setActiveNudge] = useState<NudgeId | null>(null);

	const isOutOfCoins = eligible && coinBalance !== null && coinBalance <= 0;
	const isLowBalance =
		eligible &&
		coinBalance !== null &&
		coinBalance > 0 &&
		coinBalance <= LOW_BALANCE_THRESHOLD;

	const candidateId: NudgeId | null = isOutOfCoins
		? "out_of_coins"
		: isLowBalance
			? "low_balance"
			: null;

	useEffect(() => {
		if (activeNudge) return;
		if (!candidateId || !canShow(candidateId)) return;
		markShown(candidateId, {
			coin_balance: coinBalance,
			trigger: candidateId,
		});
		setActiveNudge(candidateId);
	}, [activeNudge, candidateId, canShow, markShown, coinBalance]);

	// Clear local visibility if balance recovers past the threshold.
	useEffect(() => {
		if (!activeNudge) return;
		if (
			activeNudge === "out_of_coins" &&
			coinBalance !== null &&
			coinBalance > 0
		) {
			setActiveNudge(null);
		}
		if (
			activeNudge === "low_balance" &&
			coinBalance !== null &&
			coinBalance > LOW_BALANCE_THRESHOLD
		) {
			setActiveNudge(null);
		}
	}, [activeNudge, coinBalance]);

	if (!activeNudge) return null;

	const isOut = activeNudge === "out_of_coins";

	const handleCta = () => {
		markClicked(activeNudge, {
			coin_balance: coinBalance,
			trigger: activeNudge,
		});
		setActiveNudge(null);
		navigate("/pricing");
	};

	const handleDismiss = () => {
		markDismissed(activeNudge, {
			coin_balance: coinBalance,
			trigger: activeNudge,
		});
		setActiveNudge(null);
	};

	return (
		<Card className="mb-4 md:mb-6 relative overflow-hidden border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-transparent p-4">
			<button
				type="button"
				onClick={handleDismiss}
				aria-label={t("common.close")}
				className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-accent"
			>
				<X className="h-3.5 w-3.5" />
			</button>
			<div className="flex flex-col sm:flex-row sm:items-center gap-3 pr-6">
				<div className="flex items-start gap-3 flex-1 min-w-0">
					<div className="p-2 rounded-full bg-amber-500/10 shrink-0">
						{isOut ? (
							<Coins className="h-4 w-4 text-amber-600" />
						) : (
							<Sparkles className="h-4 w-4 text-amber-600" />
						)}
					</div>
					<div className="min-w-0">
						<p className="text-sm font-semibold text-foreground">
							{isOut
								? t("nudges.outOfCoins.title")
								: t("nudges.lowBalance.title")}
						</p>
						<p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
							{isOut
								? t("nudges.outOfCoins.description")
								: t("nudges.lowBalance.description", {
										balance: coinBalance ?? 0,
									})}
						</p>
					</div>
				</div>
				<Button
					size="sm"
					onClick={handleCta}
					className="shrink-0 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0"
				>
					{t("nudges.outOfCoins.cta")}
				</Button>
			</div>
		</Card>
	);
}
